import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PlatformLayout } from '../../components/layout/PlatformLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useAuth } from '../../contexts/AuthContext';
import { Switch } from '../../components/ui/switch';
import {
  ArrowLeft, Users, Brain, FileText, Building, FolderKanban, Mail, Phone,
  CheckCircle2, XCircle, Clock, Power, PowerOff, Mic, Globe, Sparkles, Zap, Inbox
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return '-';
  try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return d; }
}

function SourceStatusBadge({ status }) {
  if (status === 'ok') return <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />OK</Badge>;
  if (status === 'error') return <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20 text-xs"><XCircle className="w-3 h-3 mr-1" />Erreur</Badge>;
  return <Badge variant="outline" className="bg-zinc-500/10 text-zinc-500 border-zinc-500/20 text-xs"><Clock className="w-3 h-3 mr-1" />Jamais syncé</Badge>;
}

export default function PlatformTenantDetailPage() {
  const { tenantId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDetail(); }, [tenantId]);

  const fetchDetail = async () => {
    try {
      const { data: d } = await axios.get(`${API}/platform/tenants/${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(d);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  const toggleActive = async () => {
    try {
      const current = data.tenant.is_active;
      await axios.patch(`${API}/platform/tenants/${tenantId}?active=${!current}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(current ? 'Tenant désactivé' : 'Tenant activé');
      fetchDetail();
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <PlatformLayout title="Détail tenant"><p className="text-muted-foreground">Chargement...</p></PlatformLayout>;
  if (!data) return <PlatformLayout title="Détail tenant"><p className="text-destructive">Tenant non trouvé</p></PlatformLayout>;

  const { tenant, users, clones, sources, stats } = data;
  const features = tenant.features || {};

  const toggleFeature = async (feature, enabled) => {
    try {
      await axios.patch(`${API}/platform/tenants/${tenantId}/features`, { [feature]: enabled }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${feature} ${enabled ? 'activé' : 'désactivé'}`);
      fetchDetail();
    } catch { toast.error('Erreur lors de la mise à jour'); }
  };

  return (
    <PlatformLayout title={`Tenant : ${tenant.name}`}>
      <div className="space-y-6">
        <Button variant="ghost" className="gap-2 -ml-2" onClick={() => navigate('/platform/tenants')} data-testid="back-to-tenants">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Button>

        {/* Tenant info */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold">{tenant.name}</h2>
            <p className="text-muted-foreground text-sm">Slug: {tenant.slug} | ID: {tenant.id}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={tenant.is_active
              ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
              : 'bg-red-500/10 text-red-600 border-red-500/20'}>
              {tenant.is_active ? 'Actif' : 'Inactif'}
            </Badge>
            <Button variant="outline" size="sm" onClick={toggleActive} data-testid="toggle-tenant-active">
              {tenant.is_active ? <><PowerOff className="w-4 h-4 mr-1.5 text-red-500" />Désactiver</> : <><Power className="w-4 h-4 mr-1.5 text-emerald-500" />Activer</>}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
          {[
            { icon: Users, label: 'Users', value: users.length, color: 'text-violet-600 bg-violet-500/10' },
            { icon: Brain, label: 'Clones', value: clones.length, color: 'text-amber-600 bg-amber-500/10' },
            { icon: FileText, label: 'Memories', value: stats.memories_total, color: 'text-emerald-600 bg-emerald-500/10' },
            { icon: FolderKanban, label: 'Dossiers', value: stats.dossiers_total, color: 'text-orange-600 bg-orange-500/10' },
            { icon: Building, label: 'Organisations', value: stats.organizations_total, color: 'text-cyan-600 bg-cyan-500/10' },
          ].map(s => (
            <Card key={s.label}><CardContent className="flex items-center gap-3 py-3">
              <div className={`p-2 rounded-lg ${s.color}`}><s.icon className="w-4 h-4" /></div>
              <div><p className="text-lg font-bold">{s.value}</p><p className="text-[11px] text-muted-foreground">{s.label}</p></div>
            </CardContent></Card>
          ))}
        </div>

        {/* Premium Features */}
        <Card data-testid="tenant-features">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" />Features Premium</CardTitle></CardHeader>
          <CardContent className="space-y-4 pt-2">
            {[
              { key: 'voice_standard', label: 'Voix Standard', desc: 'TTS OpenAI pour les clones', icon: Mic, color: 'text-blue-500' },
              { key: 'voice_clone', label: 'Voix Clonée (Premium)', desc: 'ElevenLabs voice cloning', icon: Mic, color: 'text-violet-500' },
              { key: 'multilingual_clone', label: 'Clone Multilingue', desc: 'Réponses dans la langue de l\'utilisateur', icon: Globe, color: 'text-emerald-500' },
              { key: 'crm_sania', label: 'CRM SAN-IA', desc: 'Enrichissement contacts via le CRM intelligent SAN-IA', icon: Zap, color: 'text-amber-500' },
              { key: 'google_drive', label: 'Google Drive', desc: 'Synchronisation et indexation de documents Google Drive', icon: FileText, color: 'text-sky-500' },
              { key: 'dropbox', label: 'Dropbox', desc: 'Synchronisation et indexation de documents Dropbox', icon: FileText, color: 'text-blue-600' },
              { key: 'whatsapp', label: 'WhatsApp', desc: 'Connexion WhatsApp via QR code (Baileys)', icon: Phone, color: 'text-green-500' },
            ].map(f => (
              <div key={f.key} className="flex items-center justify-between py-2 border-b last:border-0" data-testid={`feature-row-${f.key}`}>
                <div className="flex items-center gap-3">
                  <f.icon className={`w-4 h-4 ${f.color}`} />
                  <div>
                    <p className="text-sm font-medium">{f.label}</p>
                    <p className="text-xs text-muted-foreground">{f.desc}</p>
                  </div>
                </div>
                <Switch
                  checked={!!features[f.key]}
                  onCheckedChange={(checked) => toggleFeature(f.key, checked)}
                  data-testid={`feature-toggle-${f.key}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Users */}
        <Card data-testid="tenant-users">
          <CardHeader className="pb-2"><CardTitle className="text-base">Utilisateurs ({users.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Dernière connexion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.first_name} {u.last_name}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{u.role}</Badge></TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs ${u.is_active ? 'text-emerald-600' : 'text-red-600'}`}>
                        {u.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(u.last_login)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Clones */}
        <Card data-testid="tenant-clones">
          <CardHeader className="pb-2"><CardTitle className="text-base">Clones ({clones.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Poste</TableHead>
                  <TableHead className="text-center">Memories</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clones.map(c => (
                  <TableRow key={c.id} data-testid={`clone-row-${c.id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-muted-foreground">{c.job_title || '-'}</TableCell>
                    <TableCell className="text-center"><Badge variant="secondary">{c.memory_count}</Badge></TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs ${c.is_active ? 'text-emerald-600' : 'text-red-600'}`}>
                        {c.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Sources */}
        <Card data-testid="tenant-sources">
          <CardHeader className="pb-2"><CardTitle className="text-base">Sources connectées ({sources.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            {sources.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">Aucune source configurée</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Dernière sync</TableHead>
                    <TableHead>Erreur</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sources.map(s => (
                    <TableRow key={s.id} data-testid={`source-row-${s.id}`}>
                      <TableCell>
                        <Badge variant="outline" className="text-xs gap-1">
                          {s.type === 'email' ? <Mail className="w-3 h-3" /> : <Phone className="w-3 h-3" />}
                          {s.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{s.label}</TableCell>
                      <TableCell><SourceStatusBadge status={s.status} /></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDate(s.last_sync)}</TableCell>
                      <TableCell className="text-sm text-red-600 max-w-[200px] truncate">{s.last_error || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </PlatformLayout>
  );
}
